import React, { useState } from "react";
import { Grid, IconButton, Tooltip, Box, Container, Button, Menu, MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import AppsIcon from '@mui/icons-material/Apps';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TextIncreaseIcon from '@mui/icons-material/TextIncrease';
import TextDecreaseIcon from '@mui/icons-material/TextDecrease';
import ContrastIcon from '@mui/icons-material/Contrast';

import { Wrapper } from "./header.styles";
import { useAccessibility } from "../../../global/context/accessibility-context";
import { useSystemLinks } from "./system-links";

export function HeaderComponent() {
  const navigate = useNavigate();
  const systemLinks = useSystemLinks();
  const [systemsAnchor, setSystemsAnchor] = useState<HTMLElement | null>(null);
  const routeParams = useParams<{ state?: string; city?: string }>();

  const buildPath = (path: string): string => {
    if (!routeParams.state || !routeParams.city) return path;
    return `/${routeParams.state}/${routeParams.city}${path}`;
  };
  const { increaseFontSize, decreaseFontSize, toggleHighContrast, highContrast } = useAccessibility();

  return (
    <Wrapper>
      <Container maxWidth="xl" disableGutters>
        <Grid container spacing={2} justifyContent="space-between" alignItems="center">
          {/* Links da esquerda com controles de acessibilidade */}
          <Grid item xs>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs="auto">
                <Box
                  onClick={() => navigate(buildPath('/acessibilidade'))}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    cursor: 'pointer',
                    '&:hover': {
                      opacity: 0.8
                    }
                  }}
                >
                  <AccessibilityNewIcon fontSize="small" />
                  <span>Acessibilidade</span>
                </Box>
              </Grid>
              <Grid item xs="auto">
                <Box
                  onClick={() => navigate(buildPath('/mapa-do-site'))}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    cursor: 'pointer',
                    '&:hover': {
                      opacity: 0.8
                    }
                  }}
                >
                  <AccountTreeIcon fontSize="small" />
                  <span>Mapa do Site</span>
                </Box>
              </Grid>
              <Grid item xs="auto">
                <Box
                  onClick={() => navigate(buildPath('/carta-servicos'))}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    cursor: 'pointer',
                    '&:hover': {
                      opacity: 0.8
                    }
                  }}
                >
                  <AssignmentOutlinedIcon fontSize="small" />
                  <span>Carta de Serviços</span>
                </Box>
              </Grid>

              {/* Accessibility Controls */}
              <Grid item xs="auto">
                <Box
                  sx={{
                    display: 'flex',
                    gap: 0.5,
                    alignItems: 'center',
                    borderLeft: 1,
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    pl: 2,
                  }}
                >
                  <Tooltip title="Diminuir fonte" arrow>
                    <IconButton
                      size="small"
                      onClick={decreaseFontSize}
                      sx={{
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        },
                      }}
                      aria-label="diminuir tamanho da fonte"
                    >
                      <TextDecreaseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Aumentar fonte" arrow>
                    <IconButton
                      size="small"
                      onClick={increaseFontSize}
                      sx={{
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        },
                      }}
                      aria-label="aumentar tamanho da fonte"
                    >
                      <TextIncreaseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={highContrast ? "Desativar alto contraste" : "Ativar alto contraste"} arrow>
                    <IconButton
                      size="small"
                      onClick={toggleHighContrast}
                      sx={{
                        color: 'white',
                        backgroundColor: highContrast ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        },
                      }}
                      aria-label="alternar alto contraste"
                    >
                      <ContrastIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
          </Grid>

          {/* Sistemas GovHub: menu com nome e objetivo de cada sistema */}
          <Grid item xs="auto">
            <Box component="nav" aria-label="Sistemas GovHub">
              <Button
                onClick={(e) => setSystemsAnchor(e.currentTarget)}
                startIcon={<AppsIcon />}
                endIcon={<KeyboardArrowDownIcon />}
                aria-haspopup="true"
                aria-expanded={Boolean(systemsAnchor)}
                aria-controls={systemsAnchor ? 'menu-sistemas' : undefined}
                sx={{
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 600,
                  textTransform: 'none',
                  py: 0,
                  px: 1.5,
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                }}
              >
                Sistemas GovHub
              </Button>
              <Menu
                id="menu-sistemas"
                anchorEl={systemsAnchor}
                open={Boolean(systemsAnchor)}
                onClose={() => setSystemsAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: { mt: 1, width: 380, maxWidth: 'calc(100vw - 32px)' } }}
                MenuListProps={{ sx: { py: 1 } }}
              >
                {systemLinks.map((link) => (
                  <MenuItem
                    key={link.label}
                    component="a"
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setSystemsAnchor(null)}
                    sx={{ alignItems: 'flex-start', gap: 0.5, py: 1.25, whiteSpace: 'normal' }}
                  >
                    <ListItemIcon sx={{ color: link.color, mt: 0.25 }}>{link.icon}</ListItemIcon>
                    <ListItemText
                      primary={link.label}
                      secondary={link.description}
                      primaryTypographyProps={{ fontWeight: 600 }}
                      secondaryTypographyProps={{ fontSize: '0.8125rem', lineHeight: 1.4 }}
                      sx={{ my: 0 }}
                    />
                    <OpenInNewIcon sx={{ fontSize: '1rem', color: 'text.secondary', mt: 0.5, flexShrink: 0 }} />
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Wrapper>
  );
}
